import { useEffect, useMemo, useState } from 'react';
import { PlaylistCreatedAndAssignedByLabel } from '../components/PlaylistCreatedAndAssignedByLabel';
import { useFullPlaylist, useUserOwnsPlaylist } from '../savedItemsQueries.migrated';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import FormattedMessage from 'I18n/components/FormattedMessage';
import StartPlaylistButton from 'academy-common-lib/features/savedItems/components/StartPlaylistButton';
import UsageTracker from '../../../utils/UsageTracker';
import { SCREENS } from '../../../constants/Tracking.migrated';
import { UserAttributeKeys } from '../../userAttributes/userAttributesTypes.migrated';
import { useUserAttribute } from '../../userAttributes/userAttributesQueries.migrated';
import { AssignPlaylistButton } from 'academy-common-lib/features/playlistAssignments/components/AssignPlaylistButton';
import I18n from 'I18n';
import { useCanUserEditPlaylist, useIsHubspotPlaylist, usePlaylistItemCardModels } from 'academy-common-lib/features/savedItems/savedItemsHooks';
import HubspotPlaylistTags from '../../hubspotPlaylists/components/HubspotPlaylistTags';
import HubspotPlaylistProfessors from '../../hubspotPlaylists/components/HubspotPlaylistProfessors';
import CreateOrEditHubspotPlaylistModal from 'academy-common-lib/features/hubspotPlaylists/components/createOrEditHubspotPlaylist/CreateOrEditHubspotPlaylistModal';
import HubspotPlaylistPublishUnpublishSwitch from '../../hubspotPlaylists/components/publishUnpublish/HubspotPlaylistPublishUnpublishSwitch';
import { List } from 'foundations-components/transitional/components/List';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { CopyButton } from 'foundations-components/transitional/components/CopyButton';
import { CertificationIcon } from 'foundations-assets/icons/components/CertificationIcon';
import { PresentationIcon } from 'foundations-assets/icons/components/PresentationIcon';
import { VideoIcon } from 'foundations-assets/icons/components/VideoIcon';
import { EditIcon } from 'foundations-assets/icons/components/EditIcon';
import { H7 } from 'foundations-components/transitional/components/H7';
import { Box } from 'foundations-components/transitional/components/Box';
import { Button } from 'foundations-components/transitional/components/Button';
import CardMenuIcon from 'academy-common-lib/features/cardMenu/components/CardMenuIcon';
import { playlistToPlaylistCardModel } from 'academy-common-lib/features/playlist/playlistUtils';
import { usePageTitle } from '../../../hooks/usePageTitle.legacy';
import { EditPlaylistModal } from 'academy-common-lib/features/savedItems/components/EditPlaylistModal';
import { isTrackCardModel } from 'academy-common-lib/features/cards/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const usePlaylistBreakdown = playlistItems => {
  const {
    cardModels
  } = usePlaylistItemCardModels({
    playlistItems
  });
  const tracksById = useMemo(() => {
    const byId = new Map();
    for (const cardModel of cardModels || []) {
      if (isTrackCardModel(cardModel)) {
        byId.set(cardModel.id, cardModel);
      }
    }
    return byId;
  }, [cardModels]);
  return useMemo(() => {
    const breakdown = {
      certifications: 0,
      lessons: 0,
      shorts: 0
    };
    for (const playlistItem of playlistItems) {
      if (playlistItem.type === 'LESSON') {
        breakdown.lessons++;
      } else if (playlistItem.type === 'TRACK') {
        const track = tracksById.get(playlistItem.itemId);
        if (track) {
          breakdown.lessons += track.lessonIds.length;
          if (track.certification) {
            breakdown.certifications++;
          }
        }
      } else if (playlistItem.type === 'SHORT_FORM_CONTENT') {
        breakdown.shorts++;
      }
    }
    return breakdown;
  }, [tracksById, playlistItems]);
};
const PlaylistBreakdown = ({
  playlistItems
}) => {
  const breakdown = usePlaylistBreakdown(playlistItems);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(H7, {
      marginBlockEnd: "200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.savedItems.playlistDetails.includes"
      })
    }), /*#__PURE__*/_jsxs(List, {
      inline: true,
      children: [/*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "200",
        marginInlineEnd: "300",
        children: [/*#__PURE__*/_jsx(CertificationIcon, {
          color: "icon-core-subtle",
          size: "300"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.savedItems.playlistDetails.numberOfCertifications",
          options: {
            count: breakdown.certifications
          }
        })]
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "200",
        marginInlineEnd: "300",
        children: [/*#__PURE__*/_jsx(PresentationIcon, {
          color: "icon-core-subtle",
          size: "300"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.savedItems.playlistDetails.numberOfLessons",
          options: {
            count: breakdown.lessons
          }
        })]
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "200",
        marginInlineEnd: "300",
        children: [/*#__PURE__*/_jsx(VideoIcon, {
          color: "icon-core-subtle",
          size: "300"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.savedItems.playlistDetails.numberOfShorts",
          options: {
            count: breakdown.shorts
          }
        })]
      })]
    })]
  });
};
const EditableTitle = ({
  playlistId,
  playlistName,
  canEdit,
  isHubspotPlaylist
}) => {
  const [isHubspotModalOpen, setIsHubspotModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const {
    isMobile
  } = useMediaQuery();
  const onEditPlaylistClick = () => {
    UsageTracker.track('playlistInteraction', {
      action: 'edit-details-click',
      playlistId
    });
  };
  const titleContent = /*#__PURE__*/_jsxs(Text, {
    as: "h4",
    textAlign: "left",
    textWrap: "pretty",
    typography: isMobile ? 'heading-300' : 'heading-600',
    children: [playlistName, "\xA0", /*#__PURE__*/_jsx(EditIcon, {
      color: "icon-core-default",
      size: "600"
    })]
  });
  if (!canEdit) {
    return /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "400",
      children: /*#__PURE__*/_jsx(Text, {
        as: "h4",
        textAlign: "left",
        textWrap: "pretty",
        typography: isMobile ? 'heading-300' : 'heading-600',
        children: playlistName
      })
    });
  }
  if (isHubspotPlaylist) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(View, {
        marginBlockEnd: "400",
        children: /*#__PURE__*/_jsx(Button, {
          onClick: () => {
            onEditPlaylistClick();
            setIsHubspotModalOpen(true);
          },
          responsive: false,
          use: "unstyled",
          children: titleContent
        })
      }), isHubspotModalOpen && /*#__PURE__*/_jsx(CreateOrEditHubspotPlaylistModal, {
        action: "edit",
        onClose: () => setIsHubspotModalOpen(false),
        playlistId: playlistId
      })]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      marginBlockEnd: "400",
      children: /*#__PURE__*/_jsx(Button, {
        onClick: () => {
          onEditPlaylistClick();
          setIsEditModalOpen(true);
        },
        responsive: false,
        use: "unstyled",
        children: /*#__PURE__*/_jsx(Text, {
          textAlign: "left",
          children: titleContent
        })
      })
    }), isEditModalOpen && /*#__PURE__*/_jsx(EditPlaylistModal, {
      onRequestClose: () => setIsEditModalOpen(false),
      playlistId: playlistId
    })]
  });
};
const TitleHeader = ({
  playlistId,
  playlistName
}) => {
  const isHubspotPlaylist = useIsHubspotPlaylist(playlistId);
  const canEdit = useCanUserEditPlaylist(playlistId);
  return /*#__PURE__*/_jsx(EditableTitle, {
    canEdit: Boolean(canEdit),
    isHubspotPlaylist: isHubspotPlaylist,
    playlistId: playlistId,
    playlistName: playlistName
  });
};
export const PlaylistDetails = ({
  playlistId
}) => {
  const {
    isDesktop,
    isMobile
  } = useMediaQuery();
  const {
    playlist,
    displayedPlaylistItems,
    playlistLoading
  } = useFullPlaylist(playlistId);
  const userIsOwner = useUserOwnsPlaylist(playlistId);
  const isHubspotPlaylist = useIsHubspotPlaylist(playlist === null || playlist === void 0 ? void 0 : playlist.id);
  const {
    setUserAttribute
  } = useUserAttribute(UserAttributeKeys.HAS_VIEWED_PLAYLIST);
  const canUserEditPlaylist = useCanUserEditPlaylist(playlist === null || playlist === void 0 ? void 0 : playlist.id);
  usePageTitle({
    i18nKey: 'learningCenter.dynamicPageTitles.playlistName',
    options: {
      playlistName: playlist === null || playlist === void 0 ? void 0 : playlist.name
    },
    skip: !(playlist !== null && playlist !== void 0 && playlist.name)
  });
  useEffect(() => {
    UsageTracker.track('pageView', {
      screen: SCREENS.PLAYLIST
    });
  }, []);
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    setUserAttribute(true);
  }, [setUserAttribute]);
  if (playlistLoading) {
    return /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      justifyContent: "center",
      padding: "600",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {
        size: "small"
      })
    });
  }
  if (!playlist || !displayedPlaylistItems) return null;
  const playlistCardModel = playlistToPlaylistCardModel(playlist);
  const trackPlaylistShare = () => {
    UsageTracker.track('playlistInteraction', {
      action: 'share-playlist',
      isOwner: userIsOwner,
      playlistId,
      location: 'playlist-details'
    });
  };
  return /*#__PURE__*/_jsxs(View, {
    children: [/*#__PURE__*/_jsx(TitleHeader, {
      playlistId: playlistId,
      playlistName: playlist.name
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: isDesktop ? 'row' : 'column',
      gap: "300",
      marginBlockEnd: "600",
      children: [/*#__PURE__*/_jsx(Box, {
        flex: 1,
        children: /*#__PURE__*/_jsx(StartPlaylistButton, {
          appearsIn: "playlist",
          block: true,
          playlistId: playlistId,
          showLoader: false
        })
      }), /*#__PURE__*/_jsxs(Flex, {
        flexShrink: 0,
        gap: "300",
        inlineSize: isMobile ? '100%' : undefined,
        children: [/*#__PURE__*/_jsx(CopyButton, {
          hintSuccess: I18n.text('learningCenter.shareThis.notification'),
          onClick: trackPlaylistShare,
          tooltipPlacement: "bottom",
          use: "secondary",
          value: window.location.href,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.common.share"
          })
        }), /*#__PURE__*/_jsx(View, {
          inlineSize: isMobile ? '100%' : undefined,
          marginBlockStart: "0",
          marginInlineStart: "0",
          children: /*#__PURE__*/_jsx(AssignPlaylistButton, {
            block: isMobile,
            playlistId: playlistId,
            use: "secondary",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.playlistAssignments.assign"
            })
          })
        }), isHubspotPlaylist && canUserEditPlaylist && !isMobile && /*#__PURE__*/_jsx(HubspotPlaylistPublishUnpublishSwitch, {
          playlist: playlist
        }), /*#__PURE__*/_jsx(CardMenuIcon, {
          item: playlistCardModel,
          placement: "bottom left",
          size: "medium"
        })]
      })]
    }), /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "400",
      children: /*#__PURE__*/_jsx(PlaylistCreatedAndAssignedByLabel, {
        playlistId: playlistId
      })
    }), /*#__PURE__*/_jsx(Text, {
      as: "p",
      marginBlockEnd: "400",
      children: playlist.description
    }), /*#__PURE__*/_jsx(PlaylistBreakdown, {
      playlistItems: displayedPlaylistItems
    }), isHubspotPlaylist && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(HubspotPlaylistTags, {
        playlist: playlist
      }), /*#__PURE__*/_jsx(HubspotPlaylistProfessors, {
        professors: playlist.professors || []
      })]
    })]
  });
};